import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import {
  StepEmulatorDevices,
  StepMediaCategory,
  StepMedia,
  StepFixedMediaCategory,
  Image,
  StepResponseMedia,
} from '../../../../_models';
import { ImageUploadComponent } from '../../../../image/image-upload.component';
import { ActivatedRoute, Router } from '@angular/router';
import { UploadOutput } from 'ngx-uploader';
import { AlertService, ImageService, StepEmulatorService, StorageService } from '../../../../_services';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-step-fixed-images',
  templateUrl: './step-fixed-images.component.html',
  styleUrls: ['./step-fixed-images.component.scss'],
})
export class StepFixedImagesComponent extends ImageUploadComponent implements OnInit {
  private readonly MOBILE_DIMENSIONS = '600 x 400 pixels';
  private readonly TABLET_DIMENSIONS = '800 x 600 pixels';
  private readonly DESKTOP_DIMENSIONS = '1200 x 800 pixels';

  @Input() stepMedia: StepMedia | StepResponseMedia;
  @Output() stepEmitImagesData = new EventEmitter<StepMedia | StepResponseMedia>();
  @ViewChild('imageFileInput', { static: false })
  imageFileInput!: ElementRef<HTMLInputElement>;
  activeDevice: StepEmulatorDevices = StepEmulatorDevices.Mobile;
  emulatorDevices = StepEmulatorDevices;
  stepMediaCategory = StepMediaCategory;
  imageUploading = { loading: false, progress: 0 };
  error: string | null = null;
  selectedCategory: StepFixedMediaCategory;
  stepFixedMediaCategory = StepFixedMediaCategory;
  maxUploadSize: number = 2;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected imageService: ImageService,
    protected alertService: AlertService,
    protected storageService: StorageService,
    protected stepEmulatorService: StepEmulatorService,
    protected sanitizer: DomSanitizer
  ) {
    super(router, location, route, imageService, alertService, storageService, sanitizer);
  }

  ngOnInit() {
    this.selectedCategory = this.stepMedia.fixedImage.category;
  }

  updateStepMedia() {
    this.stepEmitImagesData.emit();
  }

  setActiveDevice(device: StepEmulatorDevices) {
    this.activeDevice = device;
  }

  handleImageUpload() {
    const fileInputElement = this.imageFileInput.nativeElement;
    let file: File | null = null;

    if (fileInputElement.files && fileInputElement.files[0]) {
      file = fileInputElement.files[0];
    }

    const validMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

    if (!validMimeTypes.includes(file.type)) {
      this.error = 'Invalid file type. Only JPEG, JPG, PNG, and GIF files are allowed.';
      return window.alert(this.error);
    }

    if (file) {
      if (file.type.startsWith('image/')) {
        this.imageUploading.loading = true;

        const reader = new FileReader();
        reader.onload = (e) => {
          const result = (e.target as FileReader).result;

          if (result && typeof result === 'string') {
            this.error = null;

            this.onImageUpload({
              type: 'addedToQueue',
              nativeFile: file,
            });
          } else {
            window.alert('Error reading image file.');
          }
        };

        reader.onprogress = (event) => {
          if (event.lengthComputable) {
            this.imageUploading.progress = (event.loaded / event.total) * 100;
          }
        };

        reader.readAsDataURL(file);
      } else {
        window.alert('Selected file is not an image.');
      }
    }
    this.imageFileInput.nativeElement.value = '';
  }

  delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async onImageUpload(output: UploadOutput) {
    this.onUploadOutput({
      type: 'addedToQueue',
      file: this.uploaderService.makeUploadFile(output.nativeFile, 0),
    });
  }

  async onUploadOutput(output: UploadOutput) {
    const imageProps: any = await this.imageService.getImageAttributes(output.file);
    if (imageProps.sizeInMB > this.maxUploadSize) {
      if (!this.checkoutImage) {
        this.alertService.error(`Image size should be less than ${this.maxUploadSize}MB`);
        this.imageUploading = { loading: false, progress: 0 };
      }
    } else {
      this.files.push({
        id: output.file.id,
        is_public: true,
        file: output.file.nativeFile,
        resourcetype: this.resourceType,
        path: output.file.name,
        ppoi: '0.5x0.5',
        height: imageProps.height,
        width: imageProps.width,
      });

      this.startUpload();
    }
  }

  startUpload(): void {
    this.loading = true;
    this.imageService.create(this.files).subscribe(
      (images: Image) => {
        this.postUploadProcess([images]);
      },
      (error) => {
        this.handleSubmitError(error);
        this.loading = false;
      }
    );
  }

  private isEmptyImage(image: Image): boolean {
    return !image || !image.id || Object.keys(image).length === 0;
  }

  private setImageForAllDevices(result: Image): void {
    Object.values(StepEmulatorDevices)
      .filter((device) => typeof device === 'number')
      .forEach((device: StepEmulatorDevices) => {
        if (this.isEmptyImage(this.stepMedia.fixedImage[device])) {
          this.stepMedia.fixedImage[device] = result[0];
        }
      });
  }

  postUploadProcess(images: Image[]) {
    if (!Array.isArray(images)) {
      images = [images];
    }

    this.imageService
      .postUploadProcess(this.files, images)
      .then((result: Image) => {
        this.alertService.success('Successfully uploaded new files');
        this.imageUploading = { loading: false, progress: 0 };
        this.stepMedia.fixedImage[this.activeDevice] = result[0];

        this.setImageForAllDevices(result);
        this.updateStepMedia();

        this.loading = false;
        this.previewFiles = [];
        this.files = [];
      })
      .catch(async (error) => {
        this.handleError(error);
        // Attempt to clean up the failed upload
        if (images[0].id) {
          try {
            await this.imageService.delete(images[0].id).toPromise();
          } catch (deleteError) {
            this.handleError(deleteError);
          }
        }

        this.loading = false;
        this.imageUploading = { loading: false, progress: 0 };
        this.previewFiles = [];
        this.files = [];
      });
  }

  updateCategory(category: StepFixedMediaCategory) {
    this.selectedCategory = category;
    this.stepMedia.fixedImage.category = category;
    this.updateStepMedia();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();

    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    if (file) {
      const fileList = new DataTransfer();
      fileList.items.add(file);
      this.imageFileInput.nativeElement.files = fileList.files;
      this.handleImageUpload();
    }
  }

  clearImage() {
    this.stepMedia.fixedImage[this.activeDevice] = {} as Image;
    this.updateStepMedia();
  }

  get isMobile() {
    return this.activeDevice === StepEmulatorDevices.Mobile;
  }

  get isTablet() {
    return this.activeDevice === StepEmulatorDevices.Tablet;
  }

  get isDesktop() {
    return this.activeDevice === StepEmulatorDevices.Desktop;
  }

  get isGallery() {
    return this.selectedCategory === StepFixedMediaCategory.Gallery;
  }

  get isWidget() {
    return this.selectedCategory === StepFixedMediaCategory.Widget;
  }

  get isUpload() {
    return this.selectedCategory === StepFixedMediaCategory.Upload;
  }

  get selectedImage() {
    return this.stepMedia.fixedImage[this.activeDevice];
  }

  getRecommendedDimensions(): string {
    if (this.isMobile) {
      return this.MOBILE_DIMENSIONS;
    } else if (this.isTablet) {
      return this.TABLET_DIMENSIONS;
    }
    return this.DESKTOP_DIMENSIONS;
  }
}
